import type { CategoryInsight, HrTeamSummary, QuestionInsight } from '../../../api/queries';
import { scoreColourOf, toneOf, type Tone } from '../../../domain/psychological-safety/team-result';

/** Team som ikke har målt på så mange dager, trenger ny måling. Samme grense som i backend. */
export const MISSING_MEASUREMENT_DAYS = 60;

export type TeamStatus = Tone | 'none';
export type TeamFilter = 'results' | 'followUp' | 'missing';

/** The colour of a score, as on every other page (domain/psychological-safety/team-result.ts) */
export const scoreLevel = (score: number): Tone => toneOf[scoreColourOf(score)];

/** Hvor langt ut på skalaen fra 1 til 5 en skår ligger, i prosent */
export const scorePercent = (score: number) => Math.min(100, Math.max(0, ((score - 1) / 4) * 100));

/** The status from the backend, which follows the score alone. A team without results gets «none». */
export const teamStatus = (team: HrTeamSummary): TeamStatus =>
  team.currentScore === null || team.currentStatus === 'zero' ? 'none' : team.currentStatus;

export const teamsWithResults = (teams: HrTeamSummary[]) =>
  teams
    .filter((team) => team.currentScore !== null)
    .sort((a, b) => (a.currentScore ?? 0) - (b.currentScore ?? 0));

/** Team med resultater som trenger oppfølging: utfordrende skår, eller for lenge siden siste måling */
export const teamsNeedingFollowUp = (teams: HrTeamSummary[]) =>
  teamsWithResults(teams).filter(
    (team) => teamStatus(team) === 'danger' || team.daysSinceLastMeasurement > MISSING_MEASUREMENT_DAYS,
  );

export const statusDistribution = (teams: HrTeamSummary[]) => {
  const counts: Record<TeamStatus, number> = { danger: 0, warning: 0, success: 0, none: 0 };
  teams.forEach((team) => {
    counts[teamStatus(team)] += 1;
  });
  return counts;
};

/** Endringen siden forrige måling, når begge finnes */
export const changeSincePrevious = (team: HrTeamSummary): number | null =>
  team.currentScore !== null && team.previousScore !== null && team.previousScore !== undefined
    ? team.currentScore - team.previousScore
    : null;

export const weakestFirst = (categories: CategoryInsight[]) =>
  [...categories].sort((a, b) => a.averageScore - b.averageScore);

export const lowestQuestions = (questions: QuestionInsight[], count = 5) =>
  [...questions].sort((a, b) => a.averageScore - b.averageScore).slice(0, count);

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

export const formatScore = (value: number, language: string, fractionDigits = 2) =>
  value.toLocaleString(locale(language), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/** Navn på team som en liste, «A, B og C», med «og N andre» når det er mange */
export const formatNames = (names: string[], and: string, andOthers: (count: number) => string) => {
  const list = names.length <= 3 ? names : [...names.slice(0, 2), andOthers(names.length - 2)];
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} ${and} ${list[list.length - 1]}`;
};
